import { useCallback, useEffect, useState } from "react";
import { api, ApiError, debugMode, lastTrace,
         type Ga4Dimension, type Ga4Result, type Property,
         type BridgeResult } from "../lib/api";
import { BridgeHealthBar, verdictOf } from "../components/BridgeHealthBar";
import { DateRangeSlider } from "../components/DateRangeSlider";
import { DebugPanel } from "../components/DebugPanel";
import { useDemo } from "../lib/demo";

const DIMS: { key: Ga4Dimension; label: string }[] = [
  { key: "landingPage", label: "Landing pages" },
  { key: "sourceMedium", label: "Source / medium" },
  { key: "device", label: "Devices" },
  { key: "country", label: "Countries" },
  { key: "date", label: "Dates" },
];

/**
 * What happens after the click.
 *
 * The funnel is nested, not three independent counts: an engaged session is
 * always a session, and a conversion always happens inside one. So each bar is
 * a strict subset of the one above it and the drop-offs between them are real.
 */
export function Funnel({ property, connectionId, onChangeSources }: {
  property: Property;
  connectionId: string | null;
  onChangeSources: () => void;
}) {
  const ga4 = property.sources.find((s) => s.kind === "ga4_bq");
  const gscSrc = property.sources.find((s) => s.kind === "gsc_bq");
  const source = ga4?.bq_project && ga4?.bq_dataset
    ? { project: ga4.bq_project, dataset: ga4.bq_dataset, location: ga4.bq_location }
    : null;

  const earliest = ga4?.earliest_date ?? isoOf(Date.now() - 90 * 86400000);
  const latest = isoOf(Date.now() - 2 * 86400000);
  const [range, setRange] = useState(() => ({
    from: isoOf(Math.max(Date.parse(earliest), Date.now() - 28 * 86400000)),
    to: latest,
  }));

  const { demo } = useDemo();
  const [dimension, setDimension] = useState<Ga4Dimension>("landingPage");
  const [channel, setChannel] = useState<"all" | "organic">("organic");
  const [keyEvents, setKeyEvents] = useState<string[]>([]);
  const [result, setResult] = useState<Ga4Result | null>(null);
  const [tab, setTab] = useState<"funnel" | "bridge">("funnel");
  const [bridgeData, setBridgeData] = useState<BridgeResult | null>(null);
  const [bridgeBusy, setBridgeBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);

  const run = useCallback(async () => {
    if (!demo && (!source || !connectionId)) return;
    setBusy(true); setError(null);
    try {
      const next = await api.ga4Explore({
        connection_id: connectionId ?? "",
        project: source?.project ?? "", dataset: source?.dataset ?? "",
        location: source?.location ?? null,
        from: range.from, to: range.to, dimension, channel, keyEvents, limit: 500,
        demo,
      });
      setResult(next);
    } catch (e) {
      const err = e as ApiError;
      setFailure(err.body);
      setError(err.hint ?? `Query failed (${err.status}${err.code ? ` ${err.code}` : ""}).`);
    } finally { setBusy(false); }
  }, [connectionId, source?.project, source?.dataset, range.from, range.to,
      dimension, channel, JSON.stringify(keyEvents), demo]);

  useEffect(() => { void run(); }, [run]);

  // The bridge needs both exports and is the more expensive scan, so it only
  // runs when its tab is open.
  const runBridge = useCallback(async () => {
    if (tab !== "bridge") return;
    if (!demo && (!connectionId || !source || !gscSrc?.bq_dataset)) return;
    setBridgeBusy(true); setError(null);
    try {
      setBridgeData(await api.bridge({
        connection_id: connectionId ?? "",
        project: source?.project ?? "",
        gscDataset: gscSrc?.bq_dataset ?? "",
        ga4Dataset: source?.dataset ?? "",
        location: source?.location ?? null,
        from: range.from, to: range.to,
        keyEvents, dimension: "query", limit: 500,
        demo,
      }));
    } catch (e) {
      const err = e as ApiError;
      setFailure(err.body);
      setError(err.hint ?? `Bridge query failed (${err.status}).`);
    } finally { setBridgeBusy(false); }
  }, [tab, connectionId, source?.project, source?.dataset, gscSrc?.bq_dataset,
      range.from, range.to, JSON.stringify(keyEvents), demo]);

  useEffect(() => { void runBridge(); }, [runBridge]);

  if (!source && !demo) {
    return (
      <div className="shell">
        <h1>Funnel</h1>
        <div className="warning">
          <strong>No GA4 export selected for {property.name}.</strong> Without it
          the funnel cannot run: the GA4 Data API returns aggregates rather than
          sessions, and session-level outcomes cannot be derived from aggregates.
        </div>
        <button onClick={onChangeSources}>Pick sources</button>
      </div>
    );
  }

  const stages = result?.stages ?? [];
  const max = Math.max(1, ...stages.map((s) => s.value));

  return (
    <div className="shell wide">
      <div className="ex-bar">
        <h1>{property.name}</h1>
        <div className="ex-bar-right">
          <label className="ex-compare">
            <input type="checkbox" checked={channel === "organic"}
                   onChange={(e) => setChannel(e.target.checked ? "organic" : "all")} />
            Organic search only
          </label>
          <button className="quiet" onClick={onChangeSources}>Sources</button>
        </div>
      </div>

      <DateRangeSlider min={earliest} max={latest} from={range.from} to={range.to}
                       onChange={(f, t) => setRange({ from: f, to: t })} />

      <div className="ex-tabs" style={{ marginBottom: ".9rem" }}>
        <button className={tab === "funnel" ? "on" : ""} onClick={() => setTab("funnel")}>
          Behaviour after the click
        </button>
        <button className={tab === "bridge" ? "on" : ""} onClick={() => setTab("bridge")}
                disabled={!demo && !gscSrc?.bq_dataset}
                title={gscSrc?.bq_dataset ? undefined : "Needs a Search Console export too"}>
          Queries by outcome
        </button>
      </div>

      {error && <div className="warning">{error}</div>}

      {result && result.eventNames.length > 0 && (
        <div className="ke">
          <span className="ke-label">Count as a conversion:</span>
          {result.eventNames.slice(0, 12)
            .filter((e) => !["page_view", "session_start", "first_visit", "user_engagement",
                             "scroll"].includes(e.name))
            .map((e) => (
              <button key={e.name}
                      className={`ke-chip ${keyEvents.includes(e.name) ? "on" : ""}`}
                      onClick={() => setKeyEvents((s) =>
                        s.includes(e.name) ? s.filter((x) => x !== e.name) : [...s, e.name])}>
                {e.name}<span className="num">{e.count.toLocaleString()}</span>
              </button>
            ))}
          {keyEvents.length === 0 && (
            <span className="ke-hint">
              Pick one and the funnel gains a conversion stage.
            </span>
          )}
        </div>
      )}

      {tab === "funnel" && result && (
        <div className={busy ? "stale" : undefined}>
          <section className="panel fn">
            {stages.map((s, i) => (
              <div className="fn-stage" key={s.name}>
                <div className="fn-meta">
                  <span className="fn-name">{s.name}</span>
                  <span className="fn-value num">{s.value.toLocaleString()}</span>
                </div>
                <div className="fn-bar">
                  <div className="fn-fill" style={{ width: `${(s.value / max) * 100}%` }} />
                </div>
                {s.rate != null && (
                  <div className="fn-rate">
                    <span className="num">{(s.rate * 100).toFixed(1)}%</span> of{" "}
                    {stages[i - 1].name.toLowerCase()}
                    <em>
                      {(stages[i - 1].value - s.value).toLocaleString()} dropped off
                    </em>
                  </div>
                )}
              </div>
            ))}
            {keyEvents.length === 0 && (
              <p className="caveat">
                No conversion event selected, so the funnel stops at engagement.
              </p>
            )}
            {!result.hasSessionLastClick && (
              <p className="caveat">
                This export predates the session-scoped traffic source record, so
                the channel split is reconstructed from event data and will not
                exactly match the GA4 interface.
              </p>
            )}
          </section>

          <div className="ex-tabs">
            {DIMS.map((d) => (
              <button key={d.key} className={d.key === dimension ? "on" : ""}
                      disabled={busy} onClick={() => setDimension(d.key)}>
                {d.label}
              </button>
            ))}
          </div>

          <div className="tbl-wrap">
            <table className="tbl ex-tbl">
              <thead>
                <tr>
                  <th className="ex-key">{DIMS.find((d) => d.key === dimension)?.label}</th>
                  <th className="r">Sessions</th>
                  <th className="r">Engaged</th>
                  <th className="r">Engagement rate</th>
                  <th className="r">Conversions</th>
                  <th className="r">Avg seconds</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r) => (
                  <tr key={r.key}>
                    <td className="cell-text ex-key" title={r.key}>{short(r.key)}</td>
                    <td className="r num">{r.sessions.toLocaleString()}</td>
                    <td className="r num">{r.engaged.toLocaleString()}</td>
                    <td className="r num">
                      {r.engagementRate == null ? "—" : `${(r.engagementRate * 100).toFixed(0)}%`}
                    </td>
                    <td className="r num">{r.keyEvents.toLocaleString()}</td>
                    <td className="r num">
                      {r.avgEngagementSeconds == null ? "—" : r.avgEngagementSeconds.toFixed(0)}
                    </td>
                  </tr>
                ))}
                {!result.rows.length && (
                  <tr>
                    <td colSpan={6} className="ex-empty">
                      <strong>No sessions in this window.</strong>
                      <span>
                        The GA4 export does not backfill, so it only contains data
                        from the day it was enabled. If you turned it on recently,
                        there is nothing here yet.
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="ex-foot">
            <span>
              <strong className="num">{result.distinct.toLocaleString()}</strong> rows in this selection.
            </span>
            <span className="ex-foot-cost num">
              {result.demo
                ? "sample data · not your property"
                : result.cached
                ? "cached · no query run"
                : `${(result.bytes / 1024 ** 2).toFixed(1)} MB scanned · $${result.estUsd.toFixed(4)}`}
            </span>
          </div>
        </div>
      )}

      {tab === "bridge" && (
        <BridgeView data={bridgeData} busy={bridgeBusy} keyEvents={keyEvents} />
      )}

      {!result && busy && tab === "funnel" && (
        <div className="ex-first-load"><span className="spinner" /> Reconstructing sessions</div>
      )}

      {(debugMode() || failure !== null) && (
        <DebugPanel trace={lastTrace} error={failure ?? undefined} />
      )}
    </div>
  );
}

/**
 * Queries ranked by what happened after the click. Modelled throughout, and
 * the health bar above decides whether the table is shown at all.
 */
function BridgeView({ data, busy, keyEvents }: {
  data: BridgeResult | null;
  busy: boolean;
  keyEvents: string[];
}) {
  if (busy && !data) {
    return <div className="ex-first-load"><span className="spinner" /> Joining Search Console to GA4</div>;
  }
  if (!data) return null;

  const v = verdictOf(data.health);
  const rows = data.rows;

  return (
    <div className={busy ? "stale" : undefined}>
      <BridgeHealthBar health={data.health} />

      {v === "unusable" ? (
        <section className="panel">
          <p className="muted">
            The table is hidden because the numbers in it would not mean anything
            yet. It appears once the two sources agree well enough to allocate
            clicks to sessions.
          </p>
        </section>
      ) : (
        <>
          <div className="tbl-wrap">
            <table className="tbl ex-tbl">
              <thead>
                <tr>
                  <th className="ex-key">Query</th>
                  <th className="r">Clicks</th>
                  <th className="r">Position</th>
                  <th className="r">Sessions <em>modelled</em></th>
                  <th className="r">Engaged <em>modelled</em></th>
                  {keyEvents.length > 0 && <th className="r">Conversions <em>modelled</em></th>}
                  <th className="r">Session per click</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className={r.isWithheld ? "row-withheld" : undefined}>
                    <td className="cell-text ex-key" title={r.key}>{r.key}</td>
                    <td className="r num">{r.clicks.toLocaleString()}</td>
                    <td className="r num">{r.position?.toFixed(1) ?? "—"}</td>
                    <td className="r num">{Math.round(r.estSessions).toLocaleString()}</td>
                    <td className="r num">{Math.round(r.estEngaged).toLocaleString()}</td>
                    {keyEvents.length > 0 && (
                      <td className="r num">{r.estKeyEvents.toFixed(1)}</td>
                    )}
                    <td className="r num">
                      {r.clickToSession == null ? "—" : r.clickToSession.toFixed(2)}
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr><td colSpan={7} className="ex-empty">
                    <strong>Nothing to join yet.</strong>
                    <span>Both exports need to cover the same days.</span>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="ex-foot">
            <span>
              The <strong>(withheld)</strong> row is clicks whose query Google
              anonymized. They are kept separate rather than spread across named
              queries, which would inflate them.
            </span>
            <span className="ex-foot-cost num">
              {data.demo
                ? "sample data · not your property"
                : data.cached
                ? "cached · no query run"
                : `${(data.bytes / 1024 ** 2).toFixed(1)} MB scanned · $${data.estUsd.toFixed(4)}`}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

const isoOf = (t: number) => new Date(t).toISOString().slice(0, 10);
const short = (s: string) => s.replace(/^https?:\/\//, "");
